// Need to import the appropriate rendering profiles because of a breaking change in v18.0.0 (https://github.com/Kitware/vtk-js/releases/tag/v18.0.0)
import "vtk.js/Sources/Rendering/Profiles/Volume";

import vtkGenericRenderWindow from "vtk.js/Sources/Rendering/Misc/GenericRenderWindow";
//import vtkOpenGLRenderWindow from 'vtk.js/Sources/Rendering/OpenGL/RenderWindow';
// Force the loading of HttpDataAccessHelper to support gzip decompression
import "vtk.js/Sources/IO/Core/DataAccessHelper/HttpDataAccessHelper";

import vtkWidgetManager from "vtk.js/Sources/Widgets/Core/WidgetManager";
import vtkPaintWidget from "../Paint/PaintWidget";
import vtkPaintFilter from "./PaintFilter";
import SegmentationPaintingMode from "./PaintFilter";
import { ViewTypes } from "vtk.js/Sources/Widgets/Core/WidgetManager/Constants";

import vtkImageMapper from "vtk.js/Sources/Rendering/Core/ImageMapper";
import vtkImageReslice from "vtk.js/Sources/Imaging/Core/ImageReslice";
import vtkPlane from "vtk.js/Sources/Common/DataModel/Plane";
import vtkImageData from "vtk.js/Sources/Common/DataModel/ImageData";
import ImageConstants from "vtk.js/Sources/Rendering/Core/ImageMapper/Constants";

import vtkPiecewiseFunction from "vtk.js/Sources/Common/DataModel/PiecewiseFunction";

import {
    getTransferFunctionByColor,
    brushSizeToRadius,
    getCorrectedPaintZ,
    getManipulatorAdjustment,
} from "./SegmentationShared";
import {
    createKonvaStage,
    attachCustomScreenshotListener,
    attachViewportScreenshotListener,
} from "./DicomViewportShared";

//// DEBUG
//import vtkConeSource from 'vtk.js/Sources/Filters/Sources/ConeSource';
//import vtkMapper from 'vtk.js/Sources/Rendering/Core/Mapper';
//import vtkActor from 'vtk.js/Sources/Rendering/Core/Actor';
import React, { useEffect, useRef, useState } from "react";
import { Vector3 } from "./Vector3";
import { vec3 } from "gl-matrix";

import {
    logDebug,
    ignore,
    debugCallback,
    isNullOrUndefined,
    getDUName,
    WidgetType,
    SlicingModeName,
} from "./Common";
import customImageInteractor from "./CustomImageInteractor";
import vtkImageSlice from "vtk.js/Sources/Rendering/Core/ImageSlice";
import vtkMath from "vtk.js/Sources/Common/Core/Math";
import vtkMatrixBuilder from "vtk.js/Sources/Common/Core/MatrixBuilder";
import vtkCoordinate from "vtk.js/Sources/Rendering/Core/Coordinate";

import { useScreenshot } from "../Screenshot/Screenshot";
import { useInteraction } from "./Interaction";
import { InterpolationType } from "vtk.js/Sources/Rendering/Core/ImageProperty/Constants";
import { useDistanceWidget } from "../Distance/Distance";
import { useAngleWidget } from "../Angle/Angle";
import useCrosshairsWidget from "../Crosshairs/Crosshairs";
import useDiameterLines from "./DiameterLines";

import { Union } from "fable-compiler/bin/fable-library/Types";


const AXIAL_EMPTY_SEGMENTATION_LABEL = -1024;
const CENTERLINE_EMPTY_SEGMENTATION_LABEL = 0;

const EDITED = 2;
const ERASED = 0;

const isDebugging = false;

function log(msg, ...params) {
    logDebug("DicomVisualization", isDebugging, msg, ...params);
}

// Global storage for manual zoom state, persists across component remounts.
// Keyed by viewport ID.
const manualZoomStateByViewport = new Map();

function debug(callback) {
    debugCallback(isDebugging, callback);
}

/**
 * F# SlicingMode union type.
 * @typedef {Union} SlicingMode
 */

/**
 * F# SegmentationSliceData type
 * @typedef {Object} SegmentationSliceData
 * @property {Int16Array} buffer - The segmentation data buffer.
 * @property {number[]} dimensions - The dimensions of the segmentation slice.
 * @property {number[]} origin - The origin coordinates of the segmentation slice.
 * @property {number[]} spacing - The spacing values of the segmentation slice.
 */

/**
 * @param {Object} props
 * @param {string} props.viewportId
 * @param {File[]} props.imageFiles
 * @param {File[]} props.maskFiles
 * @param {boolean} props.showMasks
 * @param {function} props.onSliceChange
 * @param {function} props.onCenterlineSliceScroll
 * @param {function} props.onTouchStart
 * @param {function} props.onTouchEnd
 * @param {Object} props.interactionModeProps
 * @param {Object} props.screenshotProps
 * @param {boolean} props.isSliceScroll
 * @param {number} [props.slicePlaneIndex]
 * @param {boolean} props.isCenterline
 * @param {Object} props.segmentationProps
 * @param {string} props.segmentationColor
 * @param {function} props.centerlineSegmentationChangedCallback
 * @param {function} props.axialSegmentationChangedCallback
 * @param {function} props.segmentationSlicesChangedCallback
 * @param {Object} props.lengthProps
 * @param {Object} props.angleProps
 * @param {boolean} [props.isVisible]
 * @param {SlicingMode} props.slicingMode
 * @param {Union} [props.activeWidget]
 * @param {Union} [props.activeMeasurement]
 * @param {CrosshairsWidgetFactory} [props.crosshairsWidgetFactory]
 * @param {Object} props.eventNames
 * @param {Object} props.imageLoader
 * @param {function} props.onBoundsAcquired
 * @param {Array.<[number, SegmentationSliceData]>} props.modifiedSegmentations
 * @param {function} props.onVisualizationReady - Callback to invoke when the visualization is ready.
 */
const DicomVisualization = ({
    viewportId,
    imageFiles: imageFiles,
    maskFiles: maskFiles,
    showMasks,
    onSliceChange,
    onCenterlineSliceScroll,
    onTouchStart,
    onTouchEnd,
    interactionModeProps,
    screenshotProps,
    isSliceScroll,
    slicePlaneIndex,
    isCenterline,
    segmentationProps,
    segmentationColor,
    centerlineSegmentationChangedCallback,
    axialSegmentationChangedCallback,
    segmentationSlicesChangedCallback,
    lengthProps,
    angleProps,
    isVisible = true,
    slicingMode,
    activeWidget = null,
    activeMeasurement = null,
    crosshairsWidgetFactory,
    eventNames,
    imageLoader: imageLoader,
    onBoundsAcquired,
    modifiedSegmentations,
    onVisualizationReady
}) => {
    const containerRef = useRef(null);
    const context = useRef(null);
    const centerlineSegmentationChangedCallbackRef = useRef(null);
    const axialSegmentationChangedCallbackRef = useRef(null);
    const segmentationSlicesChangedCallbackRef = useRef(null);
    const maskFilesRef = useRef(maskFiles);
    const modifiedSegmentationsRef = useRef(modifiedSegmentations);
    const ignoreSliceChangeRef = useRef(false);
    const brushStrokeCompletedRef = useRef(false);
    const renderScheduledRef = useRef(false);
    const lastPaintPointRef = useRef(null);
    // Interaction handlers bind once (deps [imageFiles, viewportId]); read live
    // segmentation settings (e.g. huSelectEnabled) through this ref to avoid a stale closure.
    const segmentationPropsRef = useRef(segmentationProps);
    // Hold-to-grow (HU tool): interval id + current expansion radius while the button is held.
    const huGrowIntervalRef = useRef(null);
    const huGrowRadiusRef = useRef(0);
    // Overall z-range the current hold has touched (across all ticks). The F# commit is
    // deferred to release, so we track min/max here and commit that whole range once.
    const huGrowRangeRef = useRef(null);
    // True for the duration of a paint stroke, so the incoming modifiedSegmentations
    // effect does not overwrite the label map the user is actively painting into.
    const paintingActiveRef = useRef(false);
    const [diameterLinesTrigger, setDiameterLinesTrigger] = useState(false);

    // Initialize manual zoom state from global Map (persists across remounts).
    const hasManualZoomRef = useRef(
        manualZoomStateByViewport.get(viewportId) || false,
    );
    const hasBeenVisibleRef = useRef(isVisible);

    const { SlicingMode } = ImageConstants;

    const render = () => {
        if (context.current?.genericRenderWindow) {
            log("Rendering");
            // OPTIMIZE:
            // This is a quick fix for the resolution issue when switching between single/quad view.
            // A better fix would be to depend on a visibility prop.
            context.current.genericRenderWindow.resize();
        }
    };

    const calculateCenterlineParallelScale = () => {
        if (
            !isCenterline ||
            !context.current?.renderer ||
            !context.current?.mapper
        ) {
            return;
        }

        const containerWidth = containerRef.current?.clientWidth;
        const containerHeight = containerRef.current?.clientHeight;

        // Don't calculate if container is not visible yet.
        if (!containerWidth || !containerHeight) {
            return;
        }

        const imageData = context.current.mapper.getInputData();
        if (!imageData) {
            return;
        }

        const bounds = imageData.getBounds();
        const containerAspect = containerWidth / containerHeight;

        // Get image dimensions based on slicing mode.
        let imageWidth, imageHeight;
        switch (getDUName(slicingMode)) {
            case SlicingModeName.SAGITTAL:
                imageWidth = bounds[3] - bounds[2]; // Y extent
                imageHeight = bounds[5] - bounds[4]; // Z extent
                break;
            case SlicingModeName.CORONAL:
                imageWidth = bounds[1] - bounds[0]; // X extent
                imageHeight = bounds[5] - bounds[4]; // Z extent
                break;
            case SlicingModeName.AXIAL:
            default:
                imageWidth = bounds[1] - bounds[0]; // X extent
                imageHeight = bounds[3] - bounds[2]; // Y extent
                break;
        }

        const imageAspect = imageWidth / imageHeight;
        const camera = context.current.renderer.getActiveCamera();

        // Set parallel scale to fit image in container.
        // Parallel scale is half the height of the view frustum.
        if (containerAspect > imageAspect) {
            // Container is wider than image, fit to height.
            camera.setParallelScale(imageHeight / 2);
        } else {
            // Container is taller than image, fit to width.
            camera.setParallelScale(imageWidth / (2 * containerAspect));
        }

        context.current.renderer.resetCameraClippingRange();
    };

    const handleResize = () => {
        const container = containerRef.current;
        const ctx = context.current;

        if (!container || !ctx?.genericRenderWindow) {
            return;
        }

        ctx.genericRenderWindow.resize();

        const { width, height } = container.getBoundingClientRect();

        for (const callback of ctx.resizeCallbacks) {
            callback(width, height);
        }

        const stage = ctx.konvaStage;
        if (stage) {
            stage.size({ width: width, height: height });
            stage.batchDraw();
        }

        // Only auto-calculate parallel scale if user hasn't manually zoomed.
        if (!hasManualZoomRef.current) {
            calculateCenterlineParallelScale();
        }

        ctx.renderWindow.render();
    };

    render();

    /**
     * @param {boolean} shouldBeCenterline
     * @param {Union[][]} modifiedMaskSlices
     * @private
     */
    const _updateMaskSlices = (maskFiles, modifiedMaskSlices) => {
        if (
            context.current !== null &&
            context.current.painter !== null &&
            modifiedMaskSlices !== null &&
            maskFiles !== null
        ) {
            const baselineScalars = imageLoader.tryGetMaskBaseline(maskFiles);

            // This should only happen if the segmentation isn't loaded yet
            if (isNullOrUndefined(baselineScalars)) return;

            // Copy baseline
            const mask = context.current.painter.getLabelMap();
            if (isNullOrUndefined(mask)) return;
            const newScalars = baselineScalars.newClone();
            newScalars.deepCopy(baselineScalars);
            const data = newScalars.getData();

            // Inject modified slices
            const [nx, ny, _] = mask.getDimensions();
            const sliceSize = nx * ny;

            for (let i = 0; i < modifiedMaskSlices.length; i++) {
                const sliceIndex = modifiedMaskSlices[i][0];
                const sliceData = modifiedMaskSlices[i][1];

                const offset = sliceIndex * sliceSize;

                if (!isCenterline) {
                    const modifiedSliceData = new Int16Array(sliceData.buffer);
                    for (let j = 0; j < sliceSize; j++) {
                        const baselineVal = data[offset + j];
                        const modifiedVal = modifiedSliceData[j];
                        if (modifiedVal > 0 && (baselineVal <= 0)) {
                            data[offset + j] = EDITED;
                        } else if ((modifiedVal <= 0) && baselineVal > 0) {
                            data[offset + j] = ERASED;
                        }
                    }
                } else {
                    data.set(sliceData.buffer, offset);
                }
            }

            mask.getPointData().setScalars(newScalars);
            mask.modified();
            setDiameterLinesTrigger((prev) => !prev);
        }
    };

    // Effect that only runs when the component mounts and unmounts.
    useEffect(() => {
        log("Mounting");
        onVisualizationReady?.([viewportId, false]);
        const container = containerRef.current;

        if (container) {
            const resizeObserver = new ResizeObserver(handleResize);
            resizeObserver.observe(container);
            context.current = {
                ...context.current,
                resizeObserver,
            };
        }

        return () => {
            // Just using a more convenient name; no magic here.
            const ctx = context.current;

            if (ctx) {
                log("Unmounting");
                const container = containerRef.current;

                const {
                    genericRenderWindow,
                    onModifiedSub,
                    huSelectSub,
                    huReleaseSub,
                    actor,
                    maskActor,
                    mapper,
                    maskMapper,
                    istyle,
                    interactor,
                    widgetManager,
                    combinedTouchStart,
                    combinedTouchEnd,
                    handleWheel,
                    resizeObserver,
                } = ctx;

                if (container) {
                    container.removeEventListener(
                        "touchstart",
                        combinedTouchStart,
                    );
                    container.removeEventListener("touchend", combinedTouchEnd);
                    container.removeEventListener(
                        "touchmove",
                        istyle.handleTouchMove,
                    );
                    container.removeEventListener("wheel", handleWheel);
                    // Remove our custom mouse move handler
                    const handleMouseMove = container._brushMouseMoveHandler;
                    if (handleMouseMove) {
                        container.removeEventListener(
                            "mousemove",
                            handleMouseMove,
                        );
                        delete container._brushMouseMoveHandler;
                    }
                }

                ctx.detachCustomScreenshotListener?.();
                //Disable interactor to prevent any animations for doing anything
                interactor?.disable();
                genericRenderWindow?.delete();

                widgetManager?.removeWidgets();
                widgetManager?.releaseFocus();

                resizeObserver?.disconnect();
                onModifiedSub?.unsubscribe();
                huSelectSub?.unsubscribe();
                huReleaseSub?.unsubscribe();
                // Stop any in-flight hold-to-grow timer.
                if (huGrowIntervalRef.current !== null) {
                    clearInterval(huGrowIntervalRef.current);
                    huGrowIntervalRef.current = null;
                }
                actor?.delete();
                mapper?.delete();
                maskActor?.delete();
                maskMapper?.delete();
                widgetManager?.delete();
                context.current = null;
            }
        };
    }, []);

    useEffect(() => {
        centerlineSegmentationChangedCallbackRef.current =
            centerlineSegmentationChangedCallback;
    }, [centerlineSegmentationChangedCallback]);

    useEffect(() => {
        axialSegmentationChangedCallbackRef.current =
            axialSegmentationChangedCallback;
    }, [axialSegmentationChangedCallback]);

    useEffect(() => {
        segmentationSlicesChangedCallbackRef.current =
            segmentationSlicesChangedCallback;
    }, [segmentationSlicesChangedCallback]);

    useEffect(() => {
        segmentationPropsRef.current = segmentationProps;
    }, [segmentationProps]);

    // Effect to set up the DICOM visualization.
    useEffect(() => {
        const container = containerRef.current;
        if (
            !context.current?.actor &&
            imageFiles &&
            imageFiles.length > 0 &&
            container
        ) {
            log(`Reading ${imageFiles.length} dicom files`);

            context.current.viewportId = viewportId;
            context.current.resizeCallbacks = new Set();

            const { stage } = createKonvaStage(container, viewportId);
            context.current.konvaStage = stage;

            // We use the wrapper here to abstract out manual RenderWindow/Renderer/OpenGLRenderWindow setup
            const genericRenderWindow = vtkGenericRenderWindow.newInstance({
                background: [0.0, 0.0, 0.0],
            });

            //var genericRenderWindow = vtkOpenGLRenderWindow.newInstance();
            genericRenderWindow.setContainer(containerRef.current);
            genericRenderWindow.resize();

            const renderer = genericRenderWindow.getRenderer();
            const renderWindow = genericRenderWindow.getRenderWindow();
            debug(() => (global.debug_renderWindow = renderWindow));

            // renderer camera to parallel projection
            renderer.getActiveCamera().setParallelProjection(true);

            // --- Set up interactor style for image slicing
            const istyle = customImageInteractor.newInstance();

            // Interaction styles don't provide the ability to distinguish between a touch and left mouse press,
            // so we have to add that functionality ourselves
            const combinedTouchStart = (e) => {
                onTouchStart(e);
                istyle.handleTouchStart(e);
            };
            const combinedTouchEnd = (e) => {
                onTouchEnd(e);
                istyle.handleTouchEnd(e);
            };

            containerRef.current.addEventListener(
                "touchstart",
                combinedTouchStart,
            );
            containerRef.current.addEventListener("touchend", combinedTouchEnd);
            containerRef.current.addEventListener(
                "touchmove",
                istyle.handleTouchMove,
            );

            istyle.setContainer(containerRef.current);
            istyle.setRenderWindow(renderWindow);
            istyle.setMouseWheelIsSliceScroll(isSliceScroll);

            if (!isNullOrUndefined(onCenterlineSliceScroll)) {
                istyle.setSliceScrollCallback(onCenterlineSliceScroll);
            } else {
                const onSliceScroll = (delta) => {
                    const currentImageData = mapper.getInputData();
                    const slicingModeAxis = mapper.getSlicingMode() % 3;
                    const step = currentImageData
                        ? currentImageData.getSpacing()[slicingModeAxis]
                        : 2;
                    istyle.recalculateDistance(renderer, delta * -step);

                    // After the distance change, clamp the focal point to the image bounds
                    // so the user cannot scroll past the first/last slice in the volume.
                    if (currentImageData) {
                        const imgBounds = currentImageData.getBounds();
                        const minBound = imgBounds[slicingModeAxis * 2];
                        const maxBound = imgBounds[slicingModeAxis * 2 + 1];

                        const camera = renderer.getActiveCamera();
                        const focalPoint = camera.getFocalPoint();
                        const currentSlice = focalPoint[slicingModeAxis];

                        if (
                            currentSlice < minBound ||
                            currentSlice > maxBound
                        ) {
                            const clampedSlice = Math.max(
                                minBound,
                                Math.min(maxBound, currentSlice),
                            );
                            const newFocalPoint = [...focalPoint];
                            newFocalPoint[slicingModeAxis] = clampedSlice;
                            camera.setFocalPoint(...newFocalPoint);

                            // Also move the camera position by the same amount so that
                            // the distance (and thus the internal state) stays consistent.
                            const offset = clampedSlice - currentSlice;
                            const position = camera.getPosition();
                            const newPosition = [...position];
                            newPosition[slicingModeAxis] += offset;
                            camera.setPosition(...newPosition);
                        }
                    }

                    ignoreSliceChangeRef.current = true;
                };
                istyle.setSliceScrollCallback(onSliceScroll);
            }

            istyle.setInteractionMode("IMAGE_SLICING");
            renderWindow.getInteractor().setInteractorStyle(istyle);

            const interactor = renderWindow.getInteractor();

            // HU SELECT — standalone press-and-HOLD handler on the viewport's interactor,
            // independent of the paint widget (a focused widget would consume the click).
            // Press -> resolve world coords via the paint widget's plane manipulator, open
            // ONE stroke, then on a timer re-run the bounded 3D grow with a steadily larger
            // radius so the selection inflates outward the longer you hold. Release closes
            // the stroke (one-step undo). Each tick commits its touched slices in a single
            // batched callback (no render storm). SELECT adds the edit label; ERASE clears
            // it where the edit label exists. Growth respects the ± HU band + edge-stop.
            const HU_GROW_TICK_MS = 120;
            const HU_GROW_STEP = 8; // voxels added to the radius per tick
            const HU_GROW_RADIUS_CEILING = 600; // loose hard cap so a long hold can't run away
            // Looser edge-stop for the point-grow (bare-tissue) path than the 150 default,
            // so mild density gradients at a junction don't block flow into connected tissue.
            // (The dilation path uses the re-anchored band below instead of this.)
            const HU_GROW_EDGE_THRESHOLD = 350;
            // Re-anchored dilation band: the local step threshold is the ± HU input; the
            // wide outer clamp around the original seed is that × this multiplier. Lets a
            // hold follow tissue whose density drifts while blocking a leap into far-off HU.
            const HU_GROW_DRIFT_MULT = 4;
            // De-speckle at release: an edit-label voxel survives only if it has at least
            // this many of its 6 face-neighbors also selected. Removes thin threads/tips
            // (the lacy soft-tissue speckle) while keeping solid regions.
            const HU_CULL_MIN_NEIGHBORS = 3;

            // Adaptive tolerance: keep each HOLD tick's growth in a healthy band. Runaway is
            // measured by ACCELERATION, not an absolute count: if a tick adds far more voxels
            // than the previous tick (a sudden jump = the fill bridged into a big same-HU
            // neighbor), roll that step back and shrink tolerance. If a tick adds very few
            // voxels it's stalling → nudge tolerance up. The FIRST tick (the initial click)
            // has no previous tick to compare against, so it is never braked — it fills at
            // exactly the typed tolerance and becomes the baseline. Bounded retries per tick.
            const HU_ADAPT_LOW = 50; // voxels/step considered stalling
            const HU_ADAPT_STEP_DOWN = 15; // HU to shrink on runaway
            const HU_ADAPT_STEP_UP = 10; // HU to widen on stall
            const HU_ADAPT_MIN = 5;
            const HU_ADAPT_MAX = 400;
            const HU_ADAPT_MAX_RETRIES = 4;
            const HU_ADAPT_ACCEL_FACTOR = 4; // tick > FACTOR × prev tick ⇒ runaway
            const HU_ADAPT_ACCEL_MIN = 200; // ignore accel below this (small-count jitter)

            // Per-tick: render the live grow and widen the hold's touched z-range. NO F#
            // commit here — that's deferred to release (one commit per hold, not per tick)
            // to avoid the per-tick churn that made "Create revision" slow.
            const renderGrow = (grow) => {
                if (!grow || grow.count === 0 || grow.zMax < 0) return;
                const range = huGrowRangeRef.current;
                if (range) {
                    if (grow.zMin < range.zMin) range.zMin = grow.zMin;
                    if (grow.zMax > range.zMax) range.zMax = grow.zMax;
                } else {
                    huGrowRangeRef.current = { zMin: grow.zMin, zMax: grow.zMax };
                }
                renderWindow.render();
            };

            // At release: extract every slice the hold touched and send it to F# in ONE
            // batched update, then render. Slices are COPIED (.slice()) so the stored
            // segmentation is a frozen snapshot, not a live view into the mask buffer.
            const commitHold = (ctx, range) => {
                if (!range || range.zMax < 0) return;
                const maskImage = ctx.painter.getOutputData();
                const [nx, ny] = maskImage.getDimensions();
                const scalars = maskImage
                    .getPointData()
                    .getScalars()
                    .getData();
                const sliceSize = nx * ny;
                const maskOrigin = maskImage.getOrigin();
                const maskSpacing = maskImage.getSpacing();
                const sliceDims = [nx, ny, 1];
                const slices = [];
                for (let z = range.zMin; z <= range.zMax; z++) {
                    const offset = z * sliceSize;
                    slices.push([
                        z,
                        {
                            buffer: scalars.slice(offset, offset + sliceSize),
                            dimensions: sliceDims,
                            origin: maskOrigin,
                            spacing: maskSpacing,
                        },
                    ]);
                }
                brushStrokeCompletedRef.current = true;
                segmentationSlicesChangedCallbackRef.current?.(slices);
                renderWindow.render();
            };

            const stopHuGrow = () => {
                if (huGrowIntervalRef.current !== null) {
                    clearInterval(huGrowIntervalRef.current);
                    huGrowIntervalRef.current = null;
                    const ctx = context.current;
                    // De-speckle the whole hold (drop thin threads/tips), then commit the
                    // final clean selection to F# ONCE over the touched range.
                    const cull = ctx?.painter?.cullThinVoxels?.(HU_CULL_MIN_NEIGHBORS);
                    const range = huGrowRangeRef.current;
                    if (range && cull && cull.zMax >= 0) {
                        if (cull.zMin < range.zMin) range.zMin = cull.zMin;
                        if (cull.zMax > range.zMax) range.zMax = cull.zMax;
                    }
                    commitHold(ctx, range);
                    // Close the single stroke so the whole hold (grow + cull) undoes in one step.
                    ctx?.painter?.endStroke();
                    ctx?.painter?.clearDilate();
                    ctx?.painter?.clearErode();
                    huGrowRangeRef.current = null;
                }
            };

            const huSelectSub = interactor.onLeftButtonPress((callData) => {
                if (!segmentationPropsRef.current?.huSelectEnabled) return;
                if (callData.pokedRenderer !== renderer) return;
                // Re-entrancy guard: ignore a new press while a hold-grow is already running.
                if (huGrowIntervalRef.current !== null) return;

                const ctx = context.current;
                if (!ctx?.painter || !ctx?.paintWidget) return;

                const apiRW = genericRenderWindow.getApiSpecificRenderWindow();
                const manipulator = ctx.paintWidget.getManipulator();
                const result = manipulator.handleEvent(callData, apiRW);
                const worldPoint = result?.worldCoords;
                if (!worldPoint || worldPoint.length < 3) {
                    console.log("HU-select: could not resolve world point from click");
                    return;
                }

                // Hold Shift to invert the tool for this whole hold: Select acts as
                // Erase and Erase acts as Select. Read at press (per-hold, not live).
                const huEraseOn =
                    !!segmentationPropsRef.current?.huEraseMode !==
                    !!callData.shiftKey;
                const tolerance =
                    segmentationPropsRef.current?.huTolerance ?? 100;
                const baseRadius =
                    segmentationPropsRef.current?.huMaxRadius ?? 60;

                // Open ONE stroke for the whole hold; ticks accumulate into it (keepStroke).
                ctx.painter.startStroke();
                huGrowRadiusRef.current = baseRadius;
                // Reset the hold's touched z-range; renderGrow widens it, stopHuGrow commits it.
                huGrowRangeRef.current = null;
                // Live tolerance the adaptive controller adjusts across the hold (select only).
                let currentTolerance = tolerance;
                // Voxels committed by the previous tick; the acceleration-based runaway test
                // compares each tick against it. null until the first tick commits (so the
                // first fill is never braked and just sets the baseline).
                let prevCount = null;

                // Edge-dilation (Step 2): only in SELECT mode AND when the click lands on an
                // existing selection. beginDilate returns true if so; each tick then advances
                // the mask's boundary one layer uniformly. Otherwise fall back to the point-
                // seeded grow (Step 1): a growing sphere from the clicked voxel.
                const useDilate =
                    !huEraseOn &&
                    ctx.painter.beginDilate(worldPoint, currentTolerance, {
                        editLabel: EDITED,
                        maxRadiusVoxels: baseRadius,
                        driftMult: HU_GROW_DRIFT_MULT,
                    });

                // Erase outside-in (Step): in ERASE mode, when the click lands on an existing
                // selection, peel the mask from its outer edge inward (erosion) instead of
                // deleting from the click point outward. Falls back to the point-seeded erase
                // (growVolume3D writing empty) if the click isn't on a selection.
                const useErode =
                    huEraseOn &&
                    ctx.painter.beginErode(worldPoint, currentTolerance, {
                        editLabel: EDITED,
                        emptyLabel: AXIAL_EMPTY_SEGMENTATION_LABEL,
                        maxRadiusVoxels: baseRadius,
                        driftMult: HU_GROW_DRIFT_MULT,
                    });

                // Run one raw grow step at a given tolerance (no adaptation) — returns the
                // grow result so the controller can inspect the voxel count.
                const rawGrowStep = (tol) => {
                    if (useDilate)
                        return ctx.painter.dilateStep(tol, huGrowRadiusRef.current);
                    if (useErode)
                        return ctx.painter.erodeStep(tol, huGrowRadiusRef.current);
                    const growOptions = huEraseOn
                        ? {
                              maxRadiusVoxels: huGrowRadiusRef.current,
                              label: AXIAL_EMPTY_SEGMENTATION_LABEL,
                              onlyReplace: EDITED,
                              edgeThreshold: HU_GROW_EDGE_THRESHOLD,
                              keepStroke: true,
                          }
                        : {
                              maxRadiusVoxels: huGrowRadiusRef.current,
                              edgeThreshold: HU_GROW_EDGE_THRESHOLD,
                              keepStroke: true,
                          };
                    return ctx.painter.growVolume3D(worldPoint, tol, growOptions);
                };

                // Adaptive controller: run a step; on runaway roll it back + shrink tolerance
                // and retry; on stall nudge tolerance up. Erase mode grows at fixed tolerance
                // (auto-widening while erasing would remove more than intended).
                const runGrowTick = () => {
                    if (huEraseOn) {
                        const grow = rawGrowStep(currentTolerance);
                        renderGrow(grow);
                        return grow;
                    }
                    let attempt = 0;
                    while (true) {
                        const mark = ctx.painter.getStrokeMark();
                        const grow = rawGrowStep(currentTolerance);
                        if (
                            grow &&
                            prevCount !== null &&
                            grow.count > HU_ADAPT_ACCEL_MIN &&
                            grow.count > HU_ADAPT_ACCEL_FACTOR * prevCount &&
                            attempt < HU_ADAPT_MAX_RETRIES &&
                            currentTolerance > HU_ADAPT_MIN
                        ) {
                            // Runaway (this tick ballooned vs the last one): undo this step,
                            // shrink tolerance, retry (never commit it, don't touch prevCount).
                            ctx.painter.rollbackStrokeTo(mark);
                            currentTolerance = Math.max(
                                HU_ADAPT_MIN,
                                currentTolerance - HU_ADAPT_STEP_DOWN,
                            );
                            attempt++;
                            continue;
                        }
                        if (grow && grow.count > 0 && grow.count < HU_ADAPT_LOW) {
                            // Stalling: ease the band up a little for subsequent ticks.
                            currentTolerance = Math.min(
                                HU_ADAPT_MAX,
                                currentTolerance + HU_ADAPT_STEP_UP,
                            );
                        }
                        renderGrow(grow);
                        // This tick becomes the baseline for the next tick's accel test.
                        if (grow) prevCount = grow.count;
                        return grow;
                    }
                };

                // Immediate first fill (so a quick click still selects), then grow on hold.
                runGrowTick();
                huGrowIntervalRef.current = setInterval(() => {
                    // Keep growing while held: bump the working radius each tick (both the
                    // point-grow sphere and the dilation shell widen). A tick that writes 0
                    // voxels is NOT the end — it just means nothing new fell inside the
                    // current radius/band this tick; a bigger radius next tick may reach the
                    // connected tissue. Only release (onLeftButtonRelease) or the hard radius
                    // ceiling stops the hold. The acceleration brake in runGrowTick guards
                    // against a runaway balloon.
                    if (huGrowRadiusRef.current >= HU_GROW_RADIUS_CEILING) {
                        stopHuGrow();
                        return;
                    }
                    huGrowRadiusRef.current = Math.min(
                        huGrowRadiusRef.current + HU_GROW_STEP,
                        HU_GROW_RADIUS_CEILING,
                    );
                    runGrowTick();
                }, HU_GROW_TICK_MS);
            });

            // Release ends the hold: stop the timer and close the stroke.
            const huReleaseSub = interactor.onLeftButtonRelease(() => {
                stopHuGrow();
            });

            // Track manual zoom changes on centerline views.
            const handleWheel = () => {
                // Check if this is a centerline view and wheel is set to zoom (not slice scroll).
                if (
                    isCenterline &&
                    istyle &&
                    !istyle.getMouseWheelIsSliceScroll()
                ) {
                    // User is zooming manually, mark the flag and save to global Map.
                    hasManualZoomRef.current = true;
                    manualZoomStateByViewport.set(viewportId, true);
                }
            };

            container.addEventListener("wheel", handleWheel);

            // BRUSH FIX: Add mouse move handler to maintain brush outline on edge slices
            // VTK's paint widget loses outline visibility when the mouse moves on boundary slices
            const handleMouseMove = (e) => {
                if (
                    !paintHandle.getVisibility() ||
                    getDUName(activeWidget) !== WidgetType.SEGMENTATION_WIDGET
                ) {
                    return;
                }

                const imageData = mapper.getInputData();
                if (!imageData) return;

                const imageBounds = imageData.getBounds();
                const imageSpacing = imageData.getSpacing();
                const slicingMode = mapper.getSlicingMode() % 3;
                const minBound = imageBounds[slicingMode * 2];
                const maxBound = imageBounds[slicingMode * 2 + 1];
                const spacing = imageSpacing[slicingMode];
                const sliceLocation = mapper.getSlice();

                const adjustment = getManipulatorAdjustment(
                    sliceLocation,
                    minBound,
                    maxBound,
                    spacing,
                );

                if (adjustment !== null) {
                    const manipulatorOrigin = paintWidget
                        .getManipulator()
                        .getUserOrigin();
                    if (manipulatorOrigin) {
                        const currentZ = manipulatorOrigin[slicingMode];
                        if (Math.abs(currentZ - adjustment) > 0.01) {
                            const center = imageData.getCenter();
                            const adjustedOrigin = [
                                center[0],
                                center[1],
                                center[2],
                            ];
                            adjustedOrigin[slicingMode] = adjustment;
                            paintWidget
                                .getManipulator()
                                .setUserOrigin(adjustedOrigin);
                            paintHandle.updateRepresentationForRender();
                        }
                    }
                }
            };

            // Store reference for cleanup and add the event listener
            container._brushMouseMoveHandler = handleMouseMove;
            container.addEventListener("mousemove", handleMouseMove);

            // --- Set up the slicing actor ---

            const actor = vtkImageSlice.newInstance();
            const mapper = vtkImageMapper.newInstance();
            const slicePlane = vtkPlane.newInstance();
            debug(() => (global.debug_actor = actor));
            debug(() => (global.debug_mapper = mapper));
            debug(() => (global.debug_renderer = renderer));
            debug(() => (global.debug_slicePlane = slicePlane));
            debug(() => (global.debug_resliceFilter = resliceFilter));
            debug(() => (global.debug_transform = transform));

            mapper.setSliceAtFocalPoint(true);

            switch (slicingMode.name) {
                case SlicingModeName.SAGITTAL:
                    mapper.setSlicingMode(SlicingMode.X);
                    break;
                case SlicingModeName.CORONAL:
                    mapper.setSlicingMode(SlicingMode.Y);
                    break;
                case SlicingModeName.AXIAL:
                default:
                    mapper.setSlicingMode(SlicingMode.Z);
                    break;
            }

            actor.setMapper(mapper);

            // --- set up default window/level ---
            actor.getProperty().setColorWindow(1200);
            actor.getProperty().setColorLevel(80);
            actor.getProperty().setInterpolationType(InterpolationType.NEAREST);

            let onModifiedSub = null;
            let imageCenter;

            // --- Segmentation ---
            const widgetManager = vtkWidgetManager.newInstance();
            widgetManager.setRenderer(renderer);

            const paintWidget = vtkPaintWidget.newInstance();
            const paintHandle = widgetManager.addWidget(
                paintWidget,
                ViewTypes.SLICE,
            );
            const painter = vtkPaintFilter.newInstance();
            painter.setPaintLabel(isCenterline ? 1 : 2);

            context.current.detachCustomScreenshotListener =
                attachCustomScreenshotListener(
                    context,
                    viewportId,
                    eventNames?.CaptureCustomScreenshot,
                );

            let strokeZBounds = null;

            paintHandle.onStartInteractionEvent(() => {
                paintingActiveRef.current = true;
                const point = paintWidget.getWidgetState().getTrueOrigin();
                const controlKey = paintWidget.getWidgetState().getControlKey();
                const sliceLocation = mapper.getSlice();
                const imageData = mapper.getInputData();
                const imageBounds = imageData ? imageData.getBounds() : null;

                // BRUSH FIX: Ensure the manipulator origin is properly set for brush outline visibility
                if (imageBounds && imageData) {
                    const slicingMode = mapper.getSlicingMode() % 3;
                    const minBound = imageBounds[slicingMode * 2];
                    const maxBound = imageBounds[slicingMode * 2 + 1];
                    const imageSpacing = imageData.getSpacing();
                    const spacing = imageSpacing[slicingMode];

                    const adjustment = getManipulatorAdjustment(
                        sliceLocation,
                        minBound,
                        maxBound,
                        spacing,
                    );

                    if (adjustment !== null) {
                        const center = imageData.getCenter();
                        const adjustedOrigin = [
                            center[0],
                            center[1],
                            center[2],
                        ];
                        adjustedOrigin[slicingMode] = adjustment;
                        paintWidget
                            .getManipulator()
                            .setUserOrigin(adjustedOrigin);
                        paintHandle.updateRepresentationForRender();
                        renderWindow.render();
                    }
                }

                // BRUSH FIX: Correct the Z coordinate for painting on boundary slices
                const correctedPoint = [...point];
                if (imageBounds && imageData) {
                    const slicingMode = mapper.getSlicingMode() % 3;
                    const minBound = imageBounds[slicingMode * 2];
                    const maxBound = imageBounds[slicingMode * 2 + 1];
                    correctedPoint[slicingMode] = getCorrectedPaintZ(
                        sliceLocation,
                        minBound,
                        maxBound,
                    );
                }
                strokeZBounds = { min: correctedPoint[2], max: correctedPoint[2] };
                painter.setLabelForClickedPoint(correctedPoint, controlKey);
                painter.setSlicingMode(mapper.getSlicingMode() % 3);
                painter.startStroke();
                painter.addPoint(correctedPoint);
                lastPaintPointRef.current = correctedPoint;
                renderWindow.render();
            });

            paintHandle.onEndInteractionEvent(() => {
                paintingActiveRef.current = false;
                lastPaintPointRef.current = null;
                painter?.endStroke()
                    ?.then(() => {
                    // Render before the React cycle so the committed stroke state
                    // is visible without waiting for Elmish dispatch to complete.
                    context.current?.renderWindow?.render();

                    // TODO: This may need to be tweaked for MPR in the future, as you could be drawing on multiple slices
                    const worldCoords = paintWidget
                        .getWidgetState()
                        .getTrueOrigin();

                    // BRUSH FIX: Use corrected coordinates for slice index calculation on boundary slices
                    const imageData = mapper.getInputData();
                    const imageBounds = imageData
                        ? imageData.getBounds()
                        : null;
                    const sliceLocation = mapper.getSlice();

                    const correctedWorldCoords = [...worldCoords];
                    if (imageBounds && imageData) {
                        const slicingMode = mapper.getSlicingMode() % 3;
                        const minBound = imageBounds[slicingMode * 2];
                        const maxBound = imageBounds[slicingMode * 2 + 1];
                        correctedWorldCoords[slicingMode] = getCorrectedPaintZ(
                            sliceLocation,
                            minBound,
                            maxBound,
                        );
                    }

                    // Zero-based slice index.
                    // WARNING: If the user moves the slice plane with CTRL+Click+Drag, this will have a fractional offset.
                    // Use Math.round since getSliceAtPosition returns a fractional world-to-index
                    // value (e.g. 238.9 for slice 239). Math.floor would give the wrong slice.
                    const sliceIdxFromMapper =
                        maskMapper.getSliceAtPosition(correctedWorldCoords);
                    const sliceIdxRounded = Math.round(sliceIdxFromMapper);

                    log(`Adding contour data for slice ${sliceIdxFromMapper}`);
                    // Extract pixel data for this slice.
                    // 1) Pull out the full vtkImageData for the label-map
                    const maskImage = painter.getOutputData();

                    // 2) Query its dimensions (number of voxels in X, Y, Z)
                    const [nx, ny, nz] = maskImage.getDimensions();

                    // 3) Grab the raw TypedArray of label values
                    const scalars = maskImage
                        .getPointData()
                        .getScalars()
                        .getData(); // e.g. Int16Array of length nx*ny*nz

                    // 4) Determine the slicing axis (0=X/Sagittal, 1=Y/Coronal, 2=Z/Axial)
                    const axisMode = mapper.getSlicingMode() % 3;

                    if (isCenterline) {
                        // Invoke the callback
                        const sliceBuffer = scalars.subarray(
                            sliceIdxRounded * nx * ny,
                            (sliceIdxRounded + 1) * nx * ny,
                        );
                        brushStrokeCompletedRef.current = true;
                        centerlineSegmentationChangedCallbackRef.current(
                            sliceIdxRounded,
                            {
                                buffer: sliceBuffer,
                                dimensions: [nx, ny, 1],
                                origin: maskImage.getOrigin(),
                                spacing: maskImage.getSpacing(),
                            },
                        );
                    } else if (axisMode === 2) {
                        const sliceBuffer = scalars.subarray(
                            sliceIdxRounded * nx * ny,
                            (sliceIdxRounded + 1) * nx * ny,
                        );
                        brushStrokeCompletedRef.current = true;
                        axialSegmentationChangedCallbackRef.current(
                            sliceIdxRounded,
                            {
                                buffer: sliceBuffer,
                                dimensions: [nx, ny, 1],
                                origin: maskImage.getOrigin(),
                                spacing: maskImage.getSpacing(),
                            },
                        );
                    } else {
                        if (!strokeZBounds) {
                            return;
                        }

                        const origin      = maskImage.getOrigin();
                        const spacing     = maskImage.getSpacing();
                        const brushRadius = paintWidget.getRadius();

                        const zMin = Math.max(0,      Math.floor((strokeZBounds.min - brushRadius - origin[2]) / spacing[2]));
                        const zMax = Math.min(nz - 1, Math.ceil( (strokeZBounds.max + brushRadius - origin[2]) / spacing[2]));

                        const sliceSize = nx * ny;
                        const changedSlices = [];
                        for (let z = zMin; z <= zMax; z++) {
                            changedSlices.push([z, {
                                buffer: scalars.subarray(z * sliceSize, (z + 1) * sliceSize),
                                dimensions: [nx, ny, 1],
                                origin: maskImage.getOrigin(),
                                spacing: maskImage.getSpacing(),
                            }]);
                        }

                        brushStrokeCompletedRef.current = true;
                        segmentationSlicesChangedCallbackRef.current?.(changedSlices);
                        strokeZBounds = null;
                    }
                });
            });

            paintHandle.onInteractionEvent(() => {
                const point = paintWidget.getWidgetState().getTrueOrigin();

                // BRUSH FIX: Correct the Z coordinate for painting on boundary slices
                const imageData = mapper.getInputData();
                const imageBounds = imageData ? imageData.getBounds() : null;
                const sliceLocation = mapper.getSlice();

                const correctedPoint = [...point];
                if (imageBounds && imageData) {
                    const slicingMode = mapper.getSlicingMode() % 3;
                    const minBound = imageBounds[slicingMode * 2];
                    const maxBound = imageBounds[slicingMode * 2 + 1];
                    correctedPoint[slicingMode] = getCorrectedPaintZ(
                        sliceLocation,
                        minBound,
                        maxBound,
                    );
                }

                const prev = lastPaintPointRef.current;
                if (prev) {
                    const dx = correctedPoint[0] - prev[0];
                    const dy = correctedPoint[1] - prev[1];
                    const dz = correctedPoint[2] - prev[2];
                    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
                    const radius = painter.getRadius();
                    if (dist > radius) {
                        const steps = Math.ceil(dist / radius);
                        for (let i = 1; i < steps; i++) {
                            const t = i / steps;
                            painter.addPoint([
                                prev[0] + t * dx,
                                prev[1] + t * dy,
                                prev[2] + t * dz,
                            ]);
                        }
                    }
                }
                painter.addPoint(correctedPoint);
                lastPaintPointRef.current = correctedPoint;
                // Throttle to one GPU flush per animation frame so rapid mousemove
                // events don't queue more renders than the display can consume.
                if (!renderScheduledRef.current) {
                    renderScheduledRef.current = true;
                    requestAnimationFrame(() => {
                        renderScheduledRef.current = false;
                        context.current?.renderWindow?.render();
                    });
                }

                if (!strokeZBounds) {
                    strokeZBounds = { min: correctedPoint[2], max: correctedPoint[2] };
                } else {
                    strokeZBounds.min = Math.min(strokeZBounds.min, correctedPoint[2]);
                    strokeZBounds.max = Math.max(strokeZBounds.max, correctedPoint[2]);
                }
            });

            if (
                getDUName(activeWidget) === WidgetType.SEGMENTATION_WIDGET &&
                segmentationProps.paintingEnabled
            ) {
                widgetManager.grabFocus(paintWidget);
            }

            // Only the brush arms the paint widget. HU select uses its own interactor
            // left-button handler, so the widget must NOT grab focus or be visible for it
            // (a focused widget consumes the left click before the interactor sees it).
            paintHandle.setVisibility(segmentationProps.paintingEnabled);
            paintHandle.updateRepresentationForRender();

            const brushRadius = brushSizeToRadius(
                segmentationProps.paintingBrushSize,
            );
            paintWidget.setRadius(brushRadius);
            painter.setRadius(brushRadius);

            const maskActor = vtkImageSlice.newInstance();
            const maskMapper = vtkImageMapper.newInstance();

            maskMapper.setSliceAtFocalPoint(true);
            switch (slicingMode.name) {
                case SlicingModeName.SAGITTAL:
                    maskMapper.setSlicingMode(SlicingMode.X);
                    break;
                case SlicingModeName.CORONAL:
                    maskMapper.setSlicingMode(SlicingMode.Y);
                    break;
                case SlicingModeName.AXIAL:
                default:
                    maskMapper.setSlicingMode(SlicingMode.Z);
                    break;
            }

            maskActor.setMapper(maskMapper);

            context.current.segmentationLabel = 1;
            maskMapper.setInputConnection(painter.getOutputPort());

            // Store all VTK objects on context so other useEffects and cleanup can access them.
            context.current = {
                ...context.current,
                genericRenderWindow,
                renderer,
                renderWindow,
                istyle,
                interactor,
                actor,
                mapper,
                maskActor,
                maskMapper,
                widgetManager,
                paintWidget,
                paintHandle,
                painter,
                huSelectSub,
                huReleaseSub,
                combinedTouchStart,
                combinedTouchEnd,
                handleWheel,
            };

            const opFun = vtkPiecewiseFunction.newInstance();
            opFun.addPoint(0, 0); // our background value, 0, will be invisible
            opFun.addPoint(0.5, 1);
            opFun.addPoint(1, 1);
            opFun.addPoint(2, 1); // value 2 = edited region, fully opaque (axial only; centerline never has value 2)

            const cFun = getTransferFunctionByColor(segmentationColor);

            const prop = maskActor.getProperty();

            prop.setRGBTransferFunction(0, cFun);
            prop.setScalarOpacity(0, opFun);

            prop.setInterpolationTypeToNearest();
            prop.setUseLookupTableScalarRange(true);
            prop.setUseLabelOutline(true);
            prop.setLabelOutlineThickness(
                segmentationProps.segmentationBorderThickness,
            );
            prop.setLabelOutlineOpacity(
                segmentationProps.segmentationBorderOpacity,
            );
            prop.setOpacity(segmentationProps.segmentationOpacity);

            imageLoader.tryGetMask(maskFiles).then((imageData) => {
                if (isNullOrUndefined(imageData)) return;
                painter.setLabelMap(imageData);
                maskMapper.setInputConnection(painter.getOutputPort());
                _updateMaskSlices(maskFiles, modifiedSegmentations);
                renderWindow.render();

                const backgroundValue = isCenterline
                    ? CENTERLINE_EMPTY_SEGMENTATION_LABEL
                    : AXIAL_EMPTY_SEGMENTATION_LABEL;

                const foundLabel = imageData
                    .getPointData()
                    .getScalars()
                    .getData()
                    .find((value) => {
                        // Find the first value that isn't a background number
                        // This might lag a touch
                        return value !== backgroundValue;
                    });

                if (typeof foundLabel !== "undefined") {
                    context.current.segmentationLabel = foundLabel;

                    const opFunNew = vtkPiecewiseFunction.newInstance();
                    opFunNew.addPoint(backgroundValue, 0); // our background value, 0, will be invisible
                    opFunNew.addPoint(context.current.segmentationLabel, 1);
                    opFunNew.addPoint(2, 1); // value 2 = edited region, fully opaque (axial only)
                    maskActor.getProperty().setScalarOpacity(0, opFunNew);
                } else {
                    console.error(
                        "Unable to determine label mask, falling back to blank segmentation",
                    );
                }
            });

            // TODO: VRM-4359 Can we share the vtkImageData across DICOM viewports? This could be useful for
            //  [Axial / Sagittal / Coronal] and [Centerline Plane A / Plane B].
            imageLoader.tryGetImage(imageFiles).then((imageData) => {
                if (isNullOrUndefined(imageData)) return;

                if (!mapper.isDeleted()) {
                    debug(() => (global.debug_imageData = imageData));
                    const baseBounds = mapper.getBounds();
                    const bounds = Vector3.ofBounds(baseBounds);
                    const minIndex = vec3.create();
                    const maxIndex = vec3.create();
                    imageData.worldToIndex(bounds.min.toVtk(), minIndex);
                    imageData.worldToIndex(bounds.max.toVtk(), maxIndex);

                    let maxSliceIndex;
                    switch (slicingMode.name) {
                        case SlicingModeName.SAGITTAL:
                            maxSliceIndex = maxIndex[0];
                            break;
                        case SlicingModeName.CORONAL:
                            maxSliceIndex = maxIndex[1];
                            break;
                        case SlicingModeName.AXIAL:
                        default:
                            maxSliceIndex = maxIndex[2];
                            break;
                    }

                    imageCenter = imageData.getCenter();

                    context.current = {
                        ...context.current,
                        imageCenter,
                    };

                    mapper.setInputData(imageData);
                    painter.setBackgroundImage(imageData);

                    if (onBoundsAcquired) {
                        const imgBounds = imageData.getBounds();
                        const slicingModeAxis = context.current.mapper.getSlicingMode() % 3;
                        const minBound = imgBounds[slicingModeAxis * 2];
                        const maxBound = imgBounds[slicingModeAxis * 2 + 1];

                        onBoundsAcquired({
                            viewportId: viewportId,
                            bounds: {
                                firstIndex: minBound,
                                lastIndex: maxBound,
                            }
                        });
                    }

                    const slicingModeAxis = mapper.getSlicingMode() % 3;

                    let initialSlice;
                    if (isCenterline) {
                        // Centerline slices start from 0, but vtkImageMapper thinks 0 is out of bounds, so we add a small offset.
                        initialSlice = 0.1;
                    } else {
                        // Get the slicing mode to determine which axis we're slicing along
                        initialSlice = imageCenter[slicingModeAxis];
                    }

                    log("Setting up slice change callback");
                    onModifiedSub = mapper.onModified(
                        /**
                         * @param {import('vtk.js/Sources/Rendering/Core/ImageMapper').default} modifiedMapper
                         */
                        function (modifiedMapper) {
                            let sliceLocation = modifiedMapper.getSlice();
                            let bounds = modifiedMapper.getBoundsForSlice();
                            maskMapper.setSlice(sliceLocation);
                            log(
                                "Calling onSliceChange with",
                                sliceLocation,
                                maxSliceIndex,
                                bounds,
                            );

                            const slicingMode = mapper.getSlicingMode() % 3;

                            if (slicingMode > -1) {
                                // The sliceLocation from getSlice() is in world coordinates when setSliceAtFocalPoint(true).
                                // We need to set the manipulator origin using world coordinates on the current slice plane.
                                const center = imageData.getCenter();
                                const imgBounds = imageData.getBounds();
                                const worldOrigin = [
                                    center[0],
                                    center[1],
                                    center[2],
                                ];

                                const minBound = imgBounds[slicingMode * 2];
                                const maxBound = imgBounds[slicingMode * 2 + 1];

                                // BRUSH FIX: For boundary slices, use a position inside the bounds for the manipulator
                                // This ensures the brush outline is visible when possible
                                const imageSpacing = imageData.getSpacing();
                                const spacing = imageSpacing[slicingMode];

                                let manipulatorZ = sliceLocation;

                                // Check whether the manipulator needs to be nudged inside the bounds
                                const adjustedZ = getManipulatorAdjustment(
                                    sliceLocation,
                                    minBound,
                                    maxBound,
                                    spacing,
                                );
                                if (adjustedZ !== null) {
                                    manipulatorZ = adjustedZ;
                                }

                                worldOrigin[slicingMode] = manipulatorZ;

                                paintWidget
                                    .getManipulator()
                                    .setUserOrigin(worldOrigin);

                                // BRUSH FIX: Also set the manipulator's normal to ensure proper orientation
                                const normal = [0, 0, 0];
                                normal[slicingMode] = 1;
                                paintWidget
                                    .getManipulator()
                                    .setUserNormal(normal);

                                painter.setSlicingMode(slicingMode);
                                paintHandle.updateRepresentationForRender();
                                maskMapper.set(
                                    mapper.get("slice", "slicingMode"),
                                );

                                renderWindow.render();
                            }

                            if (onSliceChange) {
                                onSliceChange({
                                    index: sliceLocation,
                                    maxIndex: maxSliceIndex,
                                    bounds,
                                });
                            }
                        },
                    );

                    // --- Add image slice actor to scene ---
                    renderer.addActor(actor);
                    if (showMasks) {
                        renderer.addActor(maskActor);
                    }

                    // DEBUG - Add cone for reference
                    // const coneSource = vtkConeSource.newInstance({ height: 100.0, center: [ 5.801562499999989, -0.3984375, -124.25 ], radius: 100, direction: [0,0,1] });
                    // const coneMapper = vtkMapper.newInstance();
                    // coneMapper.setInputConnection(coneSource.getOutputPort());
                    // const coneActor = vtkActor.newInstance();
                    // coneActor.setMapper(coneMapper);
                    // renderer.addActor(coneActor);
                    // global.coneSource = coneSource;
                    // global.coneActor = coneActor;

                    // --- Reset camera and render the scene ---

                    let camera = renderer.getActiveCamera();
                    debug(() => (global.debug_camera = camera));

                    switch (slicingMode.name) {
                        case SlicingModeName.SAGITTAL:
                            camera.setDirectionOfProjection(1, 0, 0);
                            renderer.resetCamera();
                            camera.setViewUp(Vector3.unitZ());
                            break;
                        case SlicingModeName.CORONAL:
                            camera.setDirectionOfProjection(0, 1, 0);
                            renderer.resetCamera();
                            camera.setViewUp(Vector3.unitZ());
                            break;
                        case SlicingModeName.AXIAL:
                        default:
                            // Set the camera to be oriented feet-to-head.
                            camera.setDirectionOfProjection(0, 0, 1);
                            renderer.resetCamera();
                            camera.setViewUp(
                                vtkMath.multiplyScalar(Vector3.unitY(), -1),
                            );
                            break;
                    }

                    // Zoom in to fit the image in the viewport.
                    if (isCenterline) {
                        // Try to calculate parallel scale now, but it will be recalculated when the container becomes visible.
                        calculateCenterlineParallelScale();
                        // If container is not visible yet (width/height = 0), use a fallback scale.
                        if (
                            !containerRef.current?.clientWidth ||
                            !containerRef.current?.clientHeight
                        ) {
                            camera.setParallelScale(
                                camera.getParallelScale() / 5,
                            );
                        }
                    } else {
                        camera.setParallelScale(
                            camera.getParallelScale() / 2.5,
                        );
                    }

                    // Reset the camera clipping range to fit the image, otherwise we won't see anything.
                    renderer.resetCameraClippingRange();

                    renderer.setActiveCamera(camera);

                    if (isCenterline) {
                        // For some reason, mapper.setSlice doesn't behave as expected for the centerline DICOMs, so
                        // we have to set the focal point instead.
                        const focalPoint = camera.getFocalPoint();
                        camera.setFocalPoint(
                            focalPoint[0],
                            focalPoint[1],
                            initialSlice,
                        );
                    } else {
                        mapper.setSlice(initialSlice);
                        maskMapper.setSlice(initialSlice);
                    }

                    log("Rendering DICOM...");
                    renderWindow.render();
                    log("Finished rendering DICOM");

                    // Store the onModified subscription so cleanup can unsubscribe.
                    context.current.onModifiedSub = onModifiedSub;

                    onVisualizationReady?.([viewportId, true]);
                } else {
                    log("Aborted rendering DICOM, image mapper was disposed");
                }
            });
        }

        // --- Expose globals so we can play with values in the dev console ---

        //global.dicomGenericRenderWindow = genericRenderWindow;
        //global.dicomRenderWindow = renderWindow;
        //global.dicomRenderer = renderer;
        //global.dicomActor = actor;
        //global.dicomMapper = mapper;
    }, [imageFiles, viewportId]);

    // Effect to update the resize observer.
    useEffect(() => {
        if (context.current) {
            if (isVisible) {
                if (context.current.resizeObserver) {
                    context.current.resizeObserver.disconnect();
                }

                const resizeObserver = new ResizeObserver(handleResize);
                resizeObserver.observe(containerRef.current);
                context.current.resizeObserver = resizeObserver;
            } else {
                if (context.current.resizeObserver) {
                    context.current.resizeObserver.disconnect();
                    context.current.resizeObserver = null;
                }
            }
        }
    }, [isVisible]);

    // Screenshot side effect
    const [captureScreenshot, ...screenshotInteraction] = useScreenshot(
        containerRef.current,
        context.current,
        null,
        screenshotProps,
    );

    // Side effect to set the mouse wheel scroll mode.
    useEffect(() => {
        if (context.current?.istyle) {
            context.current.istyle.setMouseWheelIsSliceScroll(isSliceScroll);
        }
    }, [isSliceScroll]);

    //Side effect to show or hide the segmentation masks
    useEffect(() => {
        if (
            context.current.renderer &&
            context.current.renderWindow &&
            context.current.maskActor
        ) {
            showMasks
                ? context.current.renderer.addActor(context.current.maskActor)
                : context.current.renderer.removeActor(
                    context.current.maskActor,
                );
            context.current.renderWindow.render();
        }
    }, [showMasks]);

    // Sync mask opacity and paint/erase widget state whenever segmentationProps change
    useEffect(() => {
        if (context.current && context.current.maskActor) {
            const cFun = getTransferFunctionByColor(segmentationColor);
            context.current.maskActor
                .getProperty()
                .setRGBTransferFunction(0, cFun);
            context.current.maskActor
                .getProperty()
                .setOpacity(segmentationProps.segmentationOpacity);
            context.current.maskActor
                .getProperty()
                .setLabelOutlineOpacity(
                    segmentationProps.segmentationBorderOpacity,
                );
            //Prevents a fully opaque border from hiding segmentation
            if (segmentationProps.segmentationBorderOpacity === 0) {
                context.current.maskActor
                    .getProperty()
                    .setLabelOutlineThickness(0);
            } else {
                context.current.maskActor
                    .getProperty()
                    .setLabelOutlineThickness(
                        segmentationProps.segmentationBorderThickness,
                    );
            }
            //Update the brush mode
            context.current.painter.setPaintingMode(getDUName(segmentationProps.paintingMode));

            if (
                getDUName(activeWidget) === WidgetType.SEGMENTATION_WIDGET &&
                segmentationProps.paintingEnabled
            ) {
                context.current.widgetManager.grabFocus(
                    context.current.paintWidget,
                );
            } else {
                context.current.widgetManager.releaseFocus();
            }
            context.current.paintHandle.setVisibility(
                segmentationProps.paintingEnabled,
            );
            context.current.paintHandle.updateRepresentationForRender();

            const brushRadius = brushSizeToRadius(
                segmentationProps.paintingBrushSize,
            );
            context.current.paintWidget.setRadius(brushRadius);
            context.current.painter.setRadius(brushRadius);

            context.current.renderWindow.render();
        }
    }, [segmentationProps, activeWidget]);

    useEffect(() => {
        modifiedSegmentationsRef.current = modifiedSegmentations;
        if (paintingActiveRef.current) {
            return;
        }
        if (context.current?.painter) {
            if (brushStrokeCompletedRef.current) {
                brushStrokeCompletedRef.current = false;
                _updateMaskSlices(maskFilesRef.current, modifiedSegmentations);
                setDiameterLinesTrigger((prev) => !prev);
            } else {
                _updateMaskSlices(maskFilesRef.current, modifiedSegmentations);
                context.current.painter.reapplyCurrentStroke();
            }
            context.current.renderWindow?.render();
        }
    }, [modifiedSegmentations]);

    useEffect(() => {
        if (!context.current) return;

        // An authoritative mask reload always wins — clear any pending brush flag
        // that may have been left set if Elmish reverted the stroke without firing
        // the modifiedSegmentations effect.
        brushStrokeCompletedRef.current = false;

        maskFilesRef.current = maskFiles;

        imageLoader.tryGetMask(maskFiles).then((vtkImage) => {
            if (isNullOrUndefined(vtkImage)) return;

            const ctx = context.current;
            if (!ctx?.painter || !ctx?.maskMapper || !ctx?.renderWindow) return;
            ctx.painter.setLabelMap(vtkImage);
            ctx.maskMapper.setInputConnection(ctx.painter.getOutputPort());
            _updateMaskSlices(maskFilesRef.current, modifiedSegmentationsRef.current);
            ctx.painter.reapplyCurrentStroke();
            ctx.renderWindow.render();
        });

    }, [maskFiles]);

    // Side effect for repositioning the camera when the slice plane index changes.
    useEffect(() => {
        // This use effect is for syncing external slice changes
        // unfortunately, changing the slice from within the component will trigger this as well
        // Thus, we ignore these changes
        if (ignoreSliceChangeRef.current) {
            ignoreSliceChangeRef.current = false;
            return;
        }

        if (
            slicingMode.name !== SlicingModeName.AXIAL ||
            !context.current?.renderer ||
            !slicePlaneIndex
        ) {
            return;
        }

        let slice = context.current.mapper.getSlice();
        let diff = slice - slicePlaneIndex;

        if (diff === 0) {
            return;
        }
        log(`Current slice: ${slice}, requested slice: ${slicePlaneIndex}`);

        // Clamp the requested slice to the image bounds to prevent scrolling past the volume.
        let clampedSlice = slicePlaneIndex;
        const imageData = context.current.mapper.getInputData();
        if (imageData) {
            const imgBounds = imageData.getBounds();
            const slicingModeAxis = context.current.mapper.getSlicingMode() % 3;
            const minBound = imgBounds[slicingModeAxis * 2];
            const maxBound = imgBounds[slicingModeAxis * 2 + 1];
            clampedSlice = Math.max(minBound, Math.min(maxBound, clampedSlice));
        }

        const camera = context.current.renderer.getActiveCamera();
        const focalPoint = [...camera.getFocalPoint()];
        const position = [...camera.getPosition()];
        const delta = clampedSlice - focalPoint[2];
        focalPoint[2] = clampedSlice;
        position[2] += delta;

        log(`Setting focal point to ${focalPoint}`);
        camera.setFocalPoint(...focalPoint);
        camera.setPosition(...position);
        context.current.renderWindow.render();
    }, [slicePlaneIndex, slicingMode]);

    // Interaction side effect
    useInteraction(
        null,
        interactionModeProps,
        null,
        null,
        screenshotInteraction,
        captureScreenshot,
        null,
        null,
        null,
        null,
    );

    // Length tool side effect
    if (lengthProps) {
        useDistanceWidget(
            context.current,
            lengthProps,
            slicePlaneIndex,
            activeWidget,
            slicingMode,
        );
    }

    // Angle tool side effect
    if (angleProps) {
        useAngleWidget(
            context.current,
            angleProps,
            slicePlaneIndex,
            activeWidget,
            slicingMode,
        );
    }

    if (!isCenterline) {
        useCrosshairsWidget(
            context.current,
            crosshairsWidgetFactory,
            activeWidget,
            slicePlaneIndex,
        );
    } else {
        useDiameterLines(
            context.current,
            slicePlaneIndex,
            centerlineSegmentationChangedCallbackRef.current,
            maskFilesRef.current,
            !!activeMeasurement,
            diameterLinesTrigger
        );
    }

    // Side effect to force rendering when the component becomes visible.
    useEffect(() => {
        if (
            context.current?.renderWindow &&
            context.current?.genericRenderWindow &&
            isVisible
        ) {
            render();
            // Recalculate parallel scale for centerline when becoming visible.
            if (isCenterline) {
                // If this is the first time becoming visible, allow auto-scaling.
                // After that, preserve user's zoom preference.
                if (!hasBeenVisibleRef.current) {
                    hasManualZoomRef.current = false;
                    manualZoomStateByViewport.set(viewportId, false);
                    hasBeenVisibleRef.current = true;
                }

                // Only recalculate if user hasn't manually zoomed.
                if (!hasManualZoomRef.current) {
                    calculateCenterlineParallelScale();
                }

                context.current.renderWindow.render();
            }
        }
    }, [isVisible]);

    // Side effect to set the slice scroll callback.
    useEffect(() => {
        if (context.current?.istyle) {
            if (isCenterline) {
                context.current.istyle.setSliceScrollCallback(
                    onCenterlineSliceScroll,
                );
            } else {
                context.current.istyle.setSliceScrollCallback(null);
            }
        }
    }, [isCenterline]);

    // Side effect to update the centerline slice scroll callback.
    useEffect(() => {
        if (isCenterline && context.current?.istyle) {
            context.current.istyle.setSliceScrollCallback(
                onCenterlineSliceScroll,
            );
        }
    }, [isCenterline, onCenterlineSliceScroll]);

    // Side effect to handle viewport screenshot capture events.
    useEffect(() => {
        return attachViewportScreenshotListener(
            context,
            viewportId,
            eventNames?.CaptureDicomVisualizationScreenshot,
        );
    }, [eventNames, viewportId]);

    return <div className="layout-view-view-container" ref={containerRef} />;
};

export default DicomVisualization;